import { useCallback } from "react"
import { useMutation } from "urql"

import type { TransferAttachmentsMutationParams, TransferSaveResult } from "../types"

import { useUpdateTransferDocuments } from "./useUpdateTransferDocuments"
import { useUpsertTransferComment } from "./useUpsertTransferComment"

import "../graphql/mutations"

import type { UploadRowStatus } from "@/components/upload/MultiFileUpload/types"
import type {
  CreateTransferFailure,
  CreateTransferInput,
  CreateTransferMutation,
  CreateTransferMutationVariables,
  CreateTransferSuccess,
} from "@/graphql/codegen/graphql"
import { CreateTransferDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateTransferReturn {
  createTransfer: (
    input: CreateTransferInput,
    attachments?: TransferAttachmentsMutationParams
  ) => Promise<TransferSaveResult>
  isCreating: boolean
  documentUploadStatuses: Record<string, UploadRowStatus>
  isUpdatingDocuments: boolean
}

export const useCreateTransfer = (): UseCreateTransferReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { upsertComment, isUpserting: isUpsertingComment } = useUpsertTransferComment()
  const { updateDocuments, documentUploadStatuses, isUpdatingDocuments } = useUpdateTransferDocuments()

  const [{ fetching }, executeCreateTransfer] = useMutation<CreateTransferMutation, CreateTransferMutationVariables>(
    CreateTransferDocument
  )

  const createTransfer = useCallback(
    async (
      input: CreateTransferInput,
      attachments?: TransferAttachmentsMutationParams
    ): Promise<TransferSaveResult> => {
      const { data, error } = await executeCreateTransfer({ input })
      const output = data?.transfer.createTransfer
      const hasError = error || output?.__typename === "CreateTransferFailure"

      if (hasError) {
        const message = error?.message ?? (output as CreateTransferFailure).error.message
        notify({ type: NotificationType.Error, message })

        return { transferId: undefined, hasDocumentUploadFailure: false }
      }

      const { transferId } = (output as CreateTransferSuccess).transfer

      if (attachments?.comment?.trim()) {
        await upsertComment({
          transferId,
          value: attachments.comment,
          existingCommentId: attachments.existingCommentId,
        })
      }

      const hasDocumentUploadFailure = await updateDocuments({
        transferId,
        documentsToUpload: attachments?.documentsToUpload,
      })

      notify({ type: NotificationType.Success, message: "Transfer created" })

      return { transferId, hasDocumentUploadFailure }
    },
    [executeCreateTransfer, upsertComment, updateDocuments, notify]
  )

  return {
    createTransfer,
    isCreating: fetching || isUpsertingComment || isUpdatingDocuments,
    documentUploadStatuses,
    isUpdatingDocuments,
  }
}
